/**
 * §6.6 Telephony — assign an owned number (or link out to /numbers to buy
 * one), inbound/outbound toggles with the outbound-requires-number rule
 * surfaced inline, record_calls with a consent warning, and a place-a-call
 * control that appears once outbound is enabled.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import { api, ApiError } from "../../lib/api";
import { Button } from "../Button";
import { Field } from "../Field";
import { Input } from "../Input";
import { Select } from "../Select";
import { formatPhone } from "../../lib/format";
import type { AgentConfig, PhoneNumber } from "../../lib/types";
import { ToggleRow } from "./ToggleRow";
import type { DraftErrors } from "./validation";

export interface TelephonySectionProps {
  config: AgentConfig;
  patch: (partial: Partial<AgentConfig>) => void;
  errors: DraftErrors;
  /** Owned numbers (GET /api/numbers); null while loading. */
  numbers: PhoneNumber[] | null;
  /** Current agent id — null on /agents/new. */
  agentId: string | null;
  /**
   * Editor has unsaved changes. The outbound endpoint validates the SAVED
   * agent, so calling with a dirty draft can 400 about a setting the user can
   * see is enabled. Placing a call is blocked until the draft is saved.
   */
  dirty: boolean;
}

export function TelephonySection({ config, patch, errors, numbers, agentId, dirty }: TelephonySectionProps) {
  // Assignable = unassigned, assigned to this agent, or currently selected
  // (so a loaded config never renders an invalid select value).
  const assignable = (numbers ?? []).filter(
    (n) =>
      n.agent_id === null ||
      (agentId !== null && n.agent_id === agentId) ||
      n.id === config.phone_number_id,
  );

  return (
    <div className="flex flex-col gap-5">
      <Field
        label="Phone number"
        error={errors.phone_number_id}
        help={
          <>
            An agent without a number is still fully testable in the browser.{" "}
            <Link to="/numbers" className="underline underline-offset-2 hover:text-bright">
              {(numbers?.length ?? 0) === 0 ? "Buy a number" : "Manage numbers"}
            </Link>
          </>
        }
      >
        <Select
          value={config.phone_number_id ?? ""}
          onChange={(event) => {
            const value = event.target.value || null;
            // Dropping the number turns outbound off rather than leaving an
            // invalid combination for save to reject.
            patch(
              value === null
                ? { phone_number_id: null, outbound_enabled: false }
                : { phone_number_id: value },
            );
          }}
        >
          <option value="">No number — browser test only</option>
          {assignable.map((n) => (
            <option key={n.id} value={n.id}>
              {formatPhone(n.e164)}
              {n.agent_id && agentId && n.agent_id === agentId ? " (assigned to this agent)" : ""}
            </option>
          ))}
        </Select>
      </Field>

      <ToggleRow
        label="Answer incoming calls"
        help="Callers who dial the assigned number reach this agent."
        checked={config.inbound_enabled}
        onChange={(inbound_enabled) => patch({ inbound_enabled })}
      />

      <ToggleRow
        label="Allow outgoing calls"
        help="Lets you start calls from this agent to a phone number."
        checked={config.outbound_enabled}
        onChange={(outbound_enabled) => patch({ outbound_enabled })}
        error={errors.outbound_enabled}
      >
        <OutboundCaller agentId={agentId} dirty={dirty} />
      </ToggleRow>

      <ToggleRow
        label="Record calls"
        help="Keep audio recordings alongside transcripts."
        checked={config.record_calls}
        onChange={(record_calls) => patch({ record_calls })}
      >
        <p className="rounded border border-warning/60 bg-warning/10 px-3 py-2 text-xs text-warning">
          Call-recording laws vary — many places require every participant's consent. Make sure the
          greeting or prompt tells callers they're being recorded.
        </p>
      </ToggleRow>
    </div>
  );
}

/**
 * Place an outbound call from this agent.
 *
 * Rendered as a child of the "Allow outgoing calls" toggle, so it only appears
 * while outbound is on (ToggleRow renders children behind `checked`).
 *
 * The button is blocked whenever the editor is dirty. POST /agents/{id}/calls
 * reads the agent's SAVED config — outbound_enabled, an assigned number, and
 * that the number is not inbound-only — so firing it mid-edit produces a 400
 * contradicting what is on screen. Better to say why up front.
 */
function OutboundCaller({ agentId, dirty }: { agentId: string | null; dirty: boolean }) {
  const [to, setTo] = useState("");
  const [calling, setCalling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placed, setPlaced] = useState<string | null>(null);

  const blocked = agentId === null ? "Save the agent before placing a call." : dirty ? "Save your changes before placing a call." : null;

  async function placeCall(): Promise<void> {
    if (!agentId) return;
    const number = to.trim();
    if (!/^\+[1-9]\d{7,14}$/.test(number)) {
      setError("Enter the number in E.164 form, e.g. +919168849484.");
      return;
    }
    setCalling(true);
    setError(null);
    setPlaced(null);
    try {
      const call = await api.calls.create(agentId, number);
      setPlaced(`Calling ${formatPhone(number)} — call ${call.id.slice(0, 8)} is ${call.status}.`);
      setTo("");
    } catch (err: unknown) {
      // The server's message is the useful one here: it distinguishes "no
      // number assigned" from "inbound-only" from Twilio's own rejection
      // (unverified destination on a trial account, no funds, bad number).
      setError(
        err instanceof ApiError
          ? err.message
          : "Could not start the call. Check the number and try again.",
      );
    } finally {
      setCalling(false);
    }
  }

  return (
    <div className="flex flex-col gap-2 rounded border border-line p-3">
      <span className="text-xs font-medium text-body">Place a call</span>
      <p className="text-xs text-muted">
        The agent dials out from its assigned number and starts talking when the call connects.
      </p>
      <div className="flex flex-wrap items-start gap-2">
        <Input
          mono
          value={to}
          placeholder="+919168849484"
          aria-label="Phone number to call, in E.164 format"
          disabled={blocked !== null || calling}
          onChange={(event) => setTo(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !calling && !blocked) void placeCall();
          }}
        />
        <Button
          variant="primary"
          loading={calling}
          disabled={blocked !== null || !to.trim()}
          onClick={() => void placeCall()}
        >
          Call now
        </Button>
      </div>
      {blocked && <p className="text-xs text-muted">{blocked}</p>}
      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
      {placed && <p className="text-xs text-body">{placed}</p>}
    </div>
  );
}
